// InternTest.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Container,
  Progress,
  Badge,
  Divider,
  Circle,
  Tag,
} from "@chakra-ui/react";

import SendTestResults from "./SendTestResults";

// Questions derived from the prompt, grouped by sections
const quizQuestions = [
  // Section 1 — Frontend (React / React Native Concepts)
  {
    questionText:
      "Section 1 — Frontend (React / React Native Concepts)\nQ1. A developer implements a ‘Pickup Day’ selector using useState, but the label updates only after navigating away and returning. Which issue best explains the behavior?",
    answerOptions: [
      { answerText: "A. The component reuses a stale closure of state.", isCorrect: false },
      { answerText: "B. The developer reassigned the state variable instead of calling its setter.", isCorrect: true },
      { answerText: "C. The virtual DOM is batching updates incorrectly.", isCorrect: false },
      { answerText: "D. The useEffect dependency array is missing variables.", isCorrect: false },
    ],
    explanation:
      "Directly reassigning state variables (instead of using the setter) won’t trigger re-renders.",
  },
  {
    questionText:
      "Section 1 — Frontend (React / React Native Concepts)\nQ2. When displaying 100 laundry orders in a FlatList, scrolling is laggy. Which optimization helps most?",
    answerOptions: [
      { answerText: "A. Use React.memo() for each list item component.", isCorrect: true },
      { answerText: "B. Wrap the list in a ScrollView.", isCorrect: false },
      { answerText: "C. Move data fetching into each row.", isCorrect: false },
      { answerText: "D. Force update the list on every render.", isCorrect: false },
    ],
    explanation: "Memoizing row components reduces unnecessary re-renders.",
  },
  {
    questionText:
      "Section 1 — Frontend (React / React Native Concepts)\nQ3. A form built with react-hook-form never shows validation errors even though a Yup schema is defined. What is the likely cause?",
    answerOptions: [
      { answerText: "A. Missing default values for fields in the Controller.", isCorrect: true },
      { answerText: "B. The form uses onBlur mode.", isCorrect: false },
      { answerText: "C. The submit handler isn’t async.", isCorrect: false },
      { answerText: "D. The schema uses the wrong field order.", isCorrect: false },
    ],
    explanation: "Controllers often require default values for proper registration & validation.",
  },
  {
    questionText:
      "Section 1 — Frontend (React / React Native Concepts)\nQ4. Push notifications from Expo work in development but fail in production. What must be verified first?",
    answerOptions: [
      { answerText: "A. Whether the Expo push token from the client device is valid and sent to the server.", isCorrect: true },
      { answerText: "B. That Twilio is configured for fallback SMS.", isCorrect: false },
      { answerText: "C. That app.json includes \"allowNotifications\": true.", isCorrect: false },
      { answerText: "D. That the project uses Firebase Messaging SDK.", isCorrect: false },
    ],
    explanation: "Valid token acquisition and transmission is the first step to verify.",
  },

  // Section 2 — Backend (Spring Boot & API Design)
  {
    questionText:
      "Section 2 — Backend (Spring Boot & API Design)\nQ5. A POST /api/pickup request returns 415 Unsupported Media Type. The controller uses @RequestBody Pickup p. How do you resolve it?",
    answerOptions: [
      { answerText: "A. Add Content-Type: application/json to the request header.", isCorrect: true },
      { answerText: "B. Replace @RequestBody with @ModelAttribute.", isCorrect: false },
      { answerText: "C. Rename the endpoint.", isCorrect: false },
      { answerText: "D. Add a ResponseEntity return type.", isCorrect: false },
    ],
    explanation: "415 indicates the server cannot process the media type; set Content-Type JSON.",
  },
  {
    questionText:
      "Section 2 — Backend (Spring Boot & API Design)\nQ6. To mark an order as delivered following REST principles, choose the best endpoint.",
    answerOptions: [
      { answerText: "A. GET /api/orders/{id}?status=delivered", isCorrect: false },
      { answerText: "B. PATCH /api/orders/{id} with {\"status\":\"DELIVERED\"}", isCorrect: true },
      { answerText: "C. POST /api/orders/delivered", isCorrect: false },
      { answerText: "D. PUT /api/delivered/{id}", isCorrect: false },
    ],
    explanation: "PATCH to update partial resource fields adheres to REST semantics.",
  },
  {
    questionText:
      "Section 2 — Backend (Spring Boot & API Design)\nQ7. In JWT-based security, the custom filter must execute before which built-in Spring Security filter?",
    answerOptions: [
      { answerText: "A. ExceptionTranslationFilter", isCorrect: false },
      { answerText: "B. UsernamePasswordAuthenticationFilter", isCorrect: true },
      { answerText: "C. CorsFilter", isCorrect: false },
      { answerText: "D. SecurityContextPersistenceFilter", isCorrect: false },
    ],
    explanation: "JWT authentication typically occurs before UsernamePasswordAuthenticationFilter.",
  },
  {
    questionText:
      "Section 2 — Backend (Spring Boot & API Design)\nQ8. Image uploads above 5 MB suddenly fail on the backend. What setting controls this?",
    answerOptions: [
      { answerText: "A. spring.servlet.multipart.max-file-size property", isCorrect: true },
      { answerText: "B. Database row limit", isCorrect: false },
      { answerText: "C. JVM heap size", isCorrect: false },
      { answerText: "D. API gateway timeout", isCorrect: false },
    ],
    explanation: "Multipart max file size configuration governs upload limits.",
  },

  // Section 3 — Database (PostgreSQL & Data Modeling)
  {
    questionText:
      "Section 3 — Database (PostgreSQL & Data Modeling)\nQ9. A join between pickups and customers for all ‘PENDING’ orders is slow. What improves it most?",
    answerOptions: [
      { answerText: "A. Add an index on pickups.status.", isCorrect: true },
      { answerText: "B. Index customers.name.", isCorrect: false },
      { answerText: "C. Replace JOIN with UNION.", isCorrect: false },
      { answerText: "D. Increase work_mem.", isCorrect: false },
    ],
    explanation: "Filtering by status benefits directly from an index on that column.",
  },
  {
    questionText:
      "Section 3 — Database (PostgreSQL & Data Modeling)\nQ10. Ensure each customer has only one active subscription. Which DB constraint works?",
    answerOptions: [
      { answerText: "A. UNIQUE (customer_id, status) WHERE status = 'ACTIVE'", isCorrect: true },
      { answerText: "B. Foreign key to customer_id.", isCorrect: false },
      { answerText: "C. DISTINCT queries in code.", isCorrect: false },
      { answerText: "D. Trigger deleting older rows.", isCorrect: false },
    ],
    explanation: "A partial unique index enforces one ACTIVE row per customer.",
  },
  {
    questionText:
      "Section 3 — Database (PostgreSQL & Data Modeling)\nQ11. To safely purge ‘CANCELLED’ orders older than 30 days:",
    answerOptions: [
      { answerText: "A. Run DELETE in production without backup.", isCorrect: false },
      { answerText: "B. Use a scheduled job inside a transaction after taking a backup.", isCorrect: true },
      { answerText: "C. Truncate the table.", isCorrect: false },
      { answerText: "D. Drop indexes first.", isCorrect: false },
    ],
    explanation: "Backups and transactional deletes ensure safe maintenance.",
  },

  // Section 4 — Debugging & API Integration
  {
    questionText:
      "Section 4 — Debugging & API Integration\nQ12. Frontend sends {id: 'sub_1'} but backend expects {subscriptionId: 'sub_1'}. How to prevent such errors?",
    answerOptions: [
      { answerText: "A. Write automated contract tests between frontend and backend.", isCorrect: true },
      { answerText: "B. Allow dynamic field mapping.", isCorrect: false },
      { answerText: "C. Log and fix manually.", isCorrect: false },
      { answerText: "D. Ignore unknown fields.", isCorrect: false },
    ],
    explanation: "Contract tests validate schemas across services early.",
  },
  {
    questionText:
      "Section 4 — Debugging & API Integration\nQ13. Android emulator cannot connect to localhost:8080. Proper fix?",
    answerOptions: [
      { answerText: "A. Use 10.0.2.2 to reach the host machine.", isCorrect: true },
      { answerText: "B. Change port number.", isCorrect: false },
      { answerText: "C. Disable CORS.", isCorrect: false },
      { answerText: "D. Use HTTPS.", isCorrect: false },
    ],
    explanation: "10.0.2.2 is a special alias in Android emulators to host localhost.",
  },

  // Section 5 — Security & Payments
  {
    questionText:
      "Section 5 — Security & Payments\nQ14. Which approach ensures PCI-compliant payment handling with Stripe?",
    answerOptions: [
      { answerText: "A. Collect card details in React Native and send to backend.", isCorrect: false },
      { answerText: "B. Use Stripe’s client-side tokenization SDK to send only paymentMethodId.", isCorrect: true },
      { answerText: "C. Store card numbers encrypted in PostgreSQL.", isCorrect: false },
      { answerText: "D. Use Twilio API for payment notifications.", isCorrect: false },
    ],
    explanation: "Tokenization avoids handling raw card data in your servers.",
  },
  {
    questionText:
      "Section 5 — Security & Payments\nQ15. Which of these must never appear in application logs?",
    answerOptions: [
      { answerText: "A. HTTP status codes", isCorrect: false },
      { answerText: "B. JWT tokens or API keys", isCorrect: true },
      { answerText: "C. Request URLs", isCorrect: false },
      { answerText: "D. Stack traces", isCorrect: false },
    ],
    explanation: "Secrets like tokens/keys must never be logged.",
  },
  {
    questionText:
      "Section 5 — Security & Payments\nQ16. If a Twilio API key is accidentally committed to GitHub, what’s the immediate first step?",
    answerOptions: [
      { answerText: "A. Delete the repository.", isCorrect: false },
      { answerText: "B. Revoke and regenerate the key.", isCorrect: true },
      { answerText: "C. Edit the commit message.", isCorrect: false },
      { answerText: "D. Add .gitignore afterwards.", isCorrect: false },
    ],
    explanation: "Revoke compromised credentials immediately to prevent abuse.",
  },

  // Section 6 — DevOps / Software Lifecycle
  {
    questionText:
      "Section 6 — DevOps / Software Lifecycle\nQ17. Your Netlify build fails, but local npm run build succeeds. What should you check first?",
    answerOptions: [
      { answerText: "A. Node version mismatch between local and Netlify.", isCorrect: true },
      { answerText: "B. Missing favicon.", isCorrect: false },
      { answerText: "C. Browser cache.", isCorrect: false },
      { answerText: "D. Git branch naming.", isCorrect: false },
    ],
    explanation: "Ensure build environment parity (Node version) across systems.",
  },
  {
    questionText:
      "Section 6 — DevOps / Software Lifecycle\nQ18. Spring Boot service on Render frequently crashes with ‘Out of Memory’. First action?",
    answerOptions: [
      { answerText: "A. Upgrade to a larger instance plan.", isCorrect: true },
      { answerText: "B. Double database pool size.", isCorrect: false },
      { answerText: "C. Disable logging.", isCorrect: false },
      { answerText: "D. Reduce max threads to 1.", isCorrect: false },
    ],
    explanation: "First ensure adequate resources; then profile and optimize.",
  },

  // Section 7 — Architecture / Design Patterns / Domain Logic
  {
    questionText:
      "Section 7 — Architecture / Design Patterns / Domain Logic\nQ19. Why is a DTO (Data Transfer Object) preferred between Controller and Entity?",
    answerOptions: [
      { answerText: "A. It reduces SQL query count.", isCorrect: false },
      { answerText: "B. It hides internal fields and prevents data exposure.", isCorrect: true },
      { answerText: "C. It increases RAM efficiency.", isCorrect: false },
      { answerText: "D. It enforces schema validation.", isCorrect: false },
    ],
    explanation: "DTOs decouple API contracts from persistence models and prevent overexposure.",
  },
  {
    questionText:
      "Section 7 — Architecture / Design Patterns / Domain Logic\nQ20. Which pattern allows partial rollback if a payment fails after an order is created?",
    answerOptions: [
      { answerText: "A. Saga pattern", isCorrect: true },
      { answerText: "B. Circuit breaker", isCorrect: false },
      { answerText: "C. Proxy", isCorrect: false },
      { answerText: "D. Factory", isCorrect: false },
    ],
    explanation: "Saga orchestrates distributed transactions with compensating actions.",
  },
];

const InternTest = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('start') === 'true') {
      setShowIntro(false);
    }
  }, [location.search]);

  const handleAnswerOptionClick = (option, questionIndex) => {
    setUserAnswers([
      ...userAnswers,
      { questionIndex, selected: option, correct: option.isCorrect },
    ]);
    if (option.isCorrect) setScore(score + 1);

    const next = currentQuestionIndex + 1;
    if (next < quizQuestions.length) setCurrentQuestionIndex(next);
    else setShowSubmitForm(true);
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setScore(0);
    setShowSubmitForm(false);
    setUserAnswers([]);
  };

  const renderDifficultyBadge = (index) => {
    const totalQ = quizQuestions.length;
    const easyCutoff = Math.ceil(totalQ / 3);
    const mediumCutoff = Math.ceil((2 * totalQ) / 3);
    if (index < easyCutoff) return <Badge colorScheme="green">Easy</Badge>;
    if (index < mediumCutoff) return <Badge colorScheme="yellow">Medium</Badge>;
    return <Badge colorScheme="red">Hard</Badge>;
  };

  const optionLetter = (idx) => String.fromCharCode(65 + idx);

  return (
    <Box bgGradient="linear(to-br, gray.50, blue.50)" minH="calc(100vh - 120px)" py={10}>
      <Container maxW="container.lg">
        <VStack spacing={8} align="stretch">
          <VStack spacing={2}>
            <Heading
              as="h1"
              size="2xl"
              textAlign="center"
              bgGradient="linear(to-r, teal.500, blue.600)"
              bgClip="text"
              letterSpacing="tight"
            >
              Intern Test — Full Stack Developer Challenge
            </Heading>
            <Text color="gray.600" fontSize="md">
              20 curated questions across Frontend, Backend, DB, Security, DevOps and Architecture.
            </Text>
          </VStack>

          {showIntro ? (
            <Box p={8} shadow="lg" borderWidth="1px" borderColor="gray.200" borderRadius="2xl" bg="white">
              <VStack spacing={4} align="stretch">
                <Heading as="h2" size="lg">Summary: What to Expect</Heading>
                <Text color="gray.700">
                  This 60-minute assessment covers practical fundamentals across:
                </Text>
                <VStack align="start" spacing={1} pl={2}>
                  <Text>• Frontend basics (React/React Native, hooks, forms, performance)</Text>
                  <Text>• Backend REST design (Spring Boot, status codes, filters)</Text>
                  <Text>• Database essentials (indexes, constraints, safe deletes)</Text>
                  <Text>• Debugging & integration (Postman, HTTP codes, emulator networking)</Text>
                  <Text>• Security & payments (JWT, Stripe, env secrets)</Text>
                  <Text>• DevOps & patterns (builds, memory, DTO/Saga basics)</Text>
                </VStack>
                <HStack spacing={4} pt={2}>
                  <Button colorScheme="teal" variant="outline" onClick={() => navigate('/intern-learn')}>
                    Preparation Guide
                  </Button>
                  <Button colorScheme="blue" onClick={() => setShowIntro(false)}>
                    Start Test
                  </Button>
                </HStack>
              </VStack>
            </Box>
          ) : showSubmitForm ? (
            <SendTestResults
              score={score}
              total={quizQuestions.length}
              userAnswers={userAnswers}
              quizQuestions={quizQuestions}
              onRetake={resetQuiz}
            />
          ) : (
            <Box p={6} shadow="lg" borderWidth="1px" borderColor="gray.200" borderRadius="2xl" bg="white">
              <VStack spacing={4} align="stretch">
                <Box>
                  <HStack justify="space-between" align="center">
                    <HStack>
                      <Tag colorScheme="blue" size="lg" borderRadius="full" px={4} py={1}>
                        Question {currentQuestionIndex + 1} of {quizQuestions.length}
                      </Tag>
                      {renderDifficultyBadge(currentQuestionIndex)}
                    </HStack>
                    <Text color="gray.600" fontSize="sm">
                      {(currentQuestionIndex / quizQuestions.length * 100).toFixed(0)}%
                    </Text>
                  </HStack>
                  <Progress
                    value={(currentQuestionIndex / quizQuestions.length) * 100}
                    size="sm"
                    colorScheme="blue"
                    mt={3}
                    borderRadius="full"
                  />
                  <Divider my={5} />
                  {(() => {
                    const raw = quizQuestions[currentQuestionIndex].questionText || "";
                    const parts = raw.split('\n');
                    const section = parts[0] || "";
                    const statement = parts.slice(1).join('\n');
                    return (
                      <>
                        {section && (
                          <Text fontSize="sm" color="gray.500" mb={1} whiteSpace="normal">
                            {section}
                          </Text>
                        )}
                        <Heading
                          as="h3"
                          size="md"
                          mt={1}
                          mb={3}
                          whiteSpace="normal"
                          lineHeight="short"
                        >
                          {statement || section}
                        </Heading>
                      </>
                    );
                  })()}
                </Box>

                <VStack spacing={2} align="stretch">
                  {quizQuestions[currentQuestionIndex].answerOptions.map((option, index) => (
                    <Button
                      key={index}
                      size="md"
                      height="auto"
                      py={3}
                      onClick={() => handleAnswerOptionClick(option, currentQuestionIndex)}
                      variant="outline"
                      borderColor="gray.200"
                      _hover={{ bg: "blue.50", borderColor: "blue.300", transform: "translateY(-1px)" }}
                      _active={{ bg: "blue.100" }}
                      transition="all 0.15s ease"
                      textAlign="left"
                      justifyContent="flex-start"
                      whiteSpace="normal"
                      fontSize="sm"
                    >
                      <HStack spacing={4} align="start">
                        <Circle size="8" bg="blue.500" color="white" fontWeight="bold">
                          {optionLetter(index)}
                        </Circle>
                        <Text fontSize="sm">{option.answerText}</Text>
                      </HStack>
                    </Button>
                  ))}
                </VStack>
              </VStack>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default InternTest;
